/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { BusinessPartnerOperationRequest, BusinessPartnerUser } from '@msdyn365-commerce/retail-proxy';
import { getOperationRequestsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnerUsersDataActions.g';

import { getCurrentBusinessPartnerUser, GetCurrentBusinessPartnerUserInput } from './get-current-business-partner-user';
import { StringExtensions } from './utilities';
import { buildCacheKey } from './utilities/utils';

/**
 * Input class for get customer data action.
 */
export class GetBusinessPartnerOperationRequestsInput implements IActionInput {
    public fromDate: Date;

    public toDate: Date;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings, fromDate: Date, toDate: Date) {
        this.apiSettings = apiSettings;
        this.fromDate = fromDate;
        this.toDate = toDate;
    }

    public getCacheKey = () => buildCacheKey('BusinessPartnerOperationRequests-cacheKey', this.apiSettings);

    public getCacheObjectType = () => 'BusinessPartnerOperationRequest[]';

    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetBusinessPartnerOperationRequestsInput => {
    const fromDate = new Date(Date.now() - 86_400_000 * 3); // 3 days ago.

    const toDate: Date = new Date(Date.now());
    return new GetBusinessPartnerOperationRequestsInput(inputData.requestContext.apiSettings, fromDate, toDate);
};

export async function getBusinessPartnerOperationRequestsAction(
    input: GetBusinessPartnerOperationRequestsInput,
    ctx: IActionContext
): Promise<BusinessPartnerOperationRequest[]> {
    if (input) {
        const userInput = new GetCurrentBusinessPartnerUserInput(ctx.requestContext.apiSettings);
        const user: BusinessPartnerUser | undefined = await getCurrentBusinessPartnerUser(userInput, ctx);

        const isB2bContact = (ctx.requestContext.user as { isB2bContact?: boolean }).isB2bContact === true;
        const userId = (isB2bContact ? user?.ContactPersonId : user?.B2BUserId) || '';
        if (user && !StringExtensions.isNullOrEmpty(userId)) {
            return getOperationRequestsAsync({ callerContext: ctx }, userId, input.fromDate, input.toDate);
        }

        return [];
    }

    ctx.trace('[getBusinessPartnerOperationRequest]Invalid input passed to action');
    return <BusinessPartnerOperationRequest[]>[];
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-business-partner-operation-requests',
    action: <IAction<BusinessPartnerOperationRequest[]>>getBusinessPartnerOperationRequestsAction,
    input: createInput
});
